/**
 * `/admin/import` — Phase 6 "Data import".
 *
 * > select the batch's rep firm, upload, map columns, validate, preview,
 * > commit. **Ship the RFMW mapping from §5.4 as a saved preset per rep firm**,
 * > so the recurring import is three clicks rather than twenty.
 *
 * Three things about this screen are not layout decisions.
 *
 * **The attestation comes before the file picker.** §8 item 5: the batch
 * attestation is recorded server-side *before any upload credential is issued*,
 * and answering "Yes" ends the flow with no credential at all. A form that
 * collected the file first and asked afterwards would have the refused content
 * in the browser's memory and a credential already spent.
 *
 * **The file never passes through the API.** The server returns a presigned
 * POST and the browser puts the bytes in S3 itself (§6.13) — the Lambda's 6 MB
 * synchronous payload ceiling makes anything else impossible for a real
 * distributor extract.
 *
 * **Commit is a loop the client drives.** Each call commits ~200 rows and
 * returns what remains; the client re-invokes until `done`. That is API
 * Gateway's 29-second integration timeout against the 25s function timeout
 * (§3b), not a progress-bar affectation.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ExportAttestation } from '@/features/export/ExportAttestation';
import { formatMoney, humanize } from '@/lib/format';
import { useBusinessUnits } from '@/lib/reference';
import { useToast } from '@/lib/toast';

interface RepFirm {
  id: string;
  name: string;
  firm_type: string;
  net_factor: string;
  is_active: boolean;
}

interface User {
  id: string;
  display_name: string;
  email: string;
  is_active: boolean;
}

interface Batch {
  batchId: string;
  netFactor: string;
  runDate: string;
  preset: { name: string; columns: Record<string, string> };
  upload: { url: string; fields: Record<string, string> };
}

interface Reconciliation {
  opportunityCount: number;
  lineCount: number;
  annualValueCustomer: number;
  annualValueNet: number;
  annualValueAllLines: number;
  alternateLineValue: number;
  statedTransactionValueTotal: number;
}

interface Report {
  rowsRead: number;
  rowsRejected: number;
  opportunities: {
    total: number;
    insert: number;
    update: number;
    /** Absent from a report parsed before session 3. */
    skipped?: number;
    arrivingWon: number;
    becomingWon: number;
  };
  reconciliation: Reconciliation;
  issues: { errors: number; warnings: number; info: number; byCode: Record<string, number> };
  conflicts: { total: number; likelySeparateDeals: number; byKind: Record<string, number> };
  stageGateBypassCount: number;
  committable: boolean;
}

interface Conflict {
  label: string;
  kind: string;
  column: string;
  rowIndexes: number[];
  sheets: string[];
  resolution: string;
  likelySeparateDeals: boolean;
  detail: string;
}

interface Issue {
  severity: 'error' | 'warning' | 'info';
  code: string;
  rowIndex: number | null;
  column: string | null;
  message: string;
}

/** A group that matched a deal marked Invalid (decision 12), and is left alone. */
interface Skipped {
  label: string;
  opportunityId: string | null;
  rowIndexes: number[];
  reason: string;
}

/** What the commit will say about a line's part (session 7 part 3). */
interface ProductWarning {
  code: string;
  key: string;
  rowIndex: number | null;
  message: string;
}

interface ParseResult {
  batchId: string;
  report: Report;
  skipped?: Skipped[];
  conflicts: Conflict[];
  issues: Issue[];
  chunkCount: number;
  productWarnings?: ProductWarning[];
  partsToCreate?: { partNumber: string; businessUnit: string }[];
}

interface CommitProgress {
  done: boolean;
  remaining?: number;
  committed?: number;
  total?: number;
  inserted: number;
  updated: number;
  skipped?: number;
  closuresAppended: number;
  linesWritten: number;
  accountsCreated: string[];
  productsCreated: string[];
  contactsCreated: number;
  warnings: { code: string; key: string; rowIndex: number | null; message: string }[];
}

type Step = 'setup' | 'uploading' | 'validated' | 'committing' | 'done';

export function AdminImport(): React.ReactElement {
  const { push } = useToast();

  const repFirms = useQuery({
    queryKey: ['rep-firms', 'import'],
    queryFn: () => api<{ items: RepFirm[] }>('/api/rep-firms?limit=200'),
  });
  const users = useQuery({
    queryKey: ['admin', 'users', 'import'],
    queryFn: () => api<{ items: User[] }>('/api/admin/users?limit=200'),
  });

  const [repFirmId, setRepFirmId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  /** `null` until answered — §8 item 1: the question has no default. */
  const [attested, setAttested] = useState<boolean | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const [step, setStep] = useState<Step>('setup');
  const [batch, setBatch] = useState<Batch | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [progress, setProgress] = useState<CommitProgress | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const firm = repFirms.data?.items.find((f) => f.id === repFirmId);

  const start = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!file) throw new Error('Choose a file first.');

      // The attestation, and only the attestation. No file, no key, no
      // credential — a "Yes" batch never receives one (§8 item 5).
      const created = await api<Batch>('/api/admin/import/batch', {
        method: 'POST',
        body: {
          exportControlledDataWillBeEntered: attested === true,
          rep_firm_id: repFirmId,
          default_owner_user_id: ownerId,
          file_name: file.name,
        },
      });
      setBatch(created);
      setStep('uploading');

      // Browser to S3, directly. The server computed the key; the client never
      // influences it, and the presigned policy is pinned to that exact key.
      const form = new FormData();
      for (const [key, value] of Object.entries(created.upload.fields)) form.append(key, value);
      // Last: S3 ignores every field after the file part.
      form.append('file', file);

      const response = await fetch(created.upload.url, { method: 'POST', body: form });
      if (!response.ok) {
        throw new Error(`S3 refused the upload (${response.status}).`);
      }

      const report = await api<ParseResult>(`/api/admin/import/${created.batchId}/parse`, {
        method: 'POST',
      });
      setParsed(report);
      setStep('validated');
    },
    onError: (error: unknown) => {
      const code = (error as { code?: string }).code;
      if (code === 'EXPORT_BLOCKED') {
        // The refusal text is Admin-configured and never hardcoded (§8).
        setRefused((error as Error).message);
        setStep('setup');
        return;
      }
      push({
        tone: 'error',
        title: error instanceof Error ? error.message : 'The import could not start.',
      });
      setStep('setup');
    },
  });

  const commit = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!batch) return;
      setStep('committing');
      // Re-invoke until done. Each call is one chunk of ~200 rows inside its
      // own transaction, so a failure loses that chunk and no other, and a
      // replay converges rather than duplicating.
      for (;;) {
        const result = await api<CommitProgress>(`/api/admin/import/${batch.batchId}/commit`, {
          method: 'POST',
        });
        setProgress(result);
        if (result.done) break;
      }
      setStep('done');
    },
    onError: (error: unknown) => {
      push({
        tone: 'error',
        title: error instanceof Error ? error.message : 'The commit failed.',
      });
      setStep('validated');
    },
  });

  const cancel = useMutation({
    mutationFn: async (): Promise<void> => {
      if (batch) await api(`/api/admin/import/${batch.batchId}/cancel`, { method: 'POST' });
      reset();
    },
  });

  const reset = (): void => {
    setBatch(null);
    setParsed(null);
    setProgress(null);
    setFile(null);
    setAttested(null);
    setStep('setup');
  };

  const canStart = repFirmId !== '' && ownerId !== '' && file !== null && attested !== null;

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-base font-semibold">Distributor import</h2>
        <p className="text-xs text-muted-foreground">
          A recurring feed, not a one-time migration. Re-importing the same file updates the same
          opportunities rather than creating new ones.
        </p>
      </header>

      {refused !== null && (
        <Card className="border-destructive p-4">
          <h3 className="text-sm font-semibold text-destructive">This import cannot proceed</h3>
          <p className="mt-2 whitespace-pre-line text-sm">{refused}</p>
          <Button variant="outline" className="mt-3" onClick={() => setRefused(null)}>
            Start again
          </Button>
        </Card>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Setup                                                             */}
      {/* ---------------------------------------------------------------- */}

      {step === 'setup' && (
        <Card className="space-y-4 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Rep firm"
              htmlFor="import-firm"
              hint={
                firm
                  ? `Net factor ${firm.net_factor}. Every value on this batch is on the ` +
                    'distributor resale basis and is converted at this factor. Change it in ' +
                    'Reference data BEFORE importing, not after: each closure stamps the factor ' +
                    'in force at the time and cannot be corrected.'
                  : undefined
              }
            >
              <Select
                id="import-firm"
                value={repFirmId}
                onChange={(event) => setRepFirmId(event.target.value)}
              >
                <option value="">Choose a firm…</option>
                {(repFirms.data?.items ?? [])
                  .filter((f) => f.is_active)
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.firm_type})
                    </option>
                  ))}
              </Select>
            </Field>

            <Field
              label="Default owner"
              htmlFor="import-owner"
              hint={
                'The file carries no Menlo owner. An owner already assigned in the app is never ' +
                'overwritten by a later import.'
              }
            >
              <Select
                id="import-owner"
                value={ownerId}
                onChange={(event) => setOwnerId(event.target.value)}
              >
                <option value="">Choose an owner…</option>
                {(users.data?.items ?? [])
                  .filter((u) => u.is_active)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name}
                    </option>
                  ))}
              </Select>
            </Field>
          </div>

          <Field
            label="File"
            htmlFor="import-file"
            hint={
              'The RFMW column set, unmodified. Sheets are found by their header row, so a ' +
              'summary tab is ignored rather than needing to be deleted first.'
            }
          >
            <input
              id="import-file"
              type="file"
              accept=".xlsx"
              className="block w-full text-sm"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>

          {/*
            The SAME component the new-opportunity form uses, and every string
            in it comes from `@menlo/shared`. §8: "the wording is load-bearing
            and must not be paraphrased" — retyping it here is exactly how two
            slightly different questions end up in one compliance record.
          */}
          <ExportAttestation value={attested} onChange={setAttested} />

          <Button disabled={!canStart || start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? 'Uploading and validating…' : 'Upload and validate'}
          </Button>
        </Card>
      )}

      {step === 'uploading' && <Card className="p-4 text-sm">Uploading to S3 and validating…</Card>}

      {/* ---------------------------------------------------------------- */}
      {/* The validation report — the gate                                  */}
      {/* ---------------------------------------------------------------- */}

      {parsed && (step === 'validated' || step === 'committing' || step === 'done') && (
        <>
          <Card className="space-y-3 p-4">
            <h3 className="text-sm font-semibold">Reconciliation</h3>
            <dl className="grid gap-3 sm:grid-cols-3">
              <Figure label="Opportunities" value={parsed.report.reconciliation.opportunityCount} />
              <Figure label="Product lines" value={parsed.report.reconciliation.lineCount} />
              <Figure
                label="Annual value, customer basis"
                value={formatMoney(parsed.report.reconciliation.annualValueCustomer)}
              />
            </dl>
            <p className="text-xs text-muted-foreground">
              Alternate lines are excluded from the value — they are competing parts for a socket a
              primary line already counts, and here they are worth{' '}
              {formatMoney(parsed.report.reconciliation.alternateLineValue)}. On the net basis the
              same population is {formatMoney(parsed.report.reconciliation.annualValueNet)}. The
              file&rsquo;s own Tran. Value column totals{' '}
              {formatMoney(parsed.report.reconciliation.statedTransactionValueTotal)} and is never
              imported; the source rounds each row to whole dollars.
            </p>
          </Card>

          <Card className="space-y-2 p-4">
            <h3 className="text-sm font-semibold">What this will do</h3>
            <ul className="space-y-1 text-sm">
              <li>
                {parsed.report.opportunities.insert} new opportunities,{' '}
                {parsed.report.opportunities.update} updated
                {(parsed.report.opportunities.skipped ?? 0) > 0 && (
                  <>
                    , {parsed.report.opportunities.skipped} skipped because they are marked Invalid
                  </>
                )}
              </li>
              <li>
                {parsed.report.opportunities.arrivingWon} arrive already won and{' '}
                {parsed.report.opportunities.becomingWon} become won — each gets a closure recorded
                as <code>IMPORTED_CHANNEL</code>, which is counted as a design win but excluded from
                win rate and win-reason analysis
              </li>
              <li>
                stage gating bypassed for {parsed.report.stageGateBypassCount} imported
                opportunities, recorded once in the audit log
              </li>
              <li>
                {parsed.chunkCount} chunk{parsed.chunkCount === 1 ? '' : 's'} of about 200 rows
              </li>
            </ul>
          </Card>

          {(parsed.skipped ?? []).length > 0 && (
            <Card className="space-y-2 p-4">
              <h3 className="text-sm font-semibold">
                Skipped — {(parsed.skipped ?? []).length} marked Invalid
              </h3>
              <p className="text-xs text-muted-foreground">
                These rows match deals someone marked Invalid — not a real opportunity. The import
                leaves them exactly as they are: no update, no new lines, never reopened. A Manager
                or an Admin can reopen one if it is real after all.
              </p>
              <ul className="space-y-2">
                {(parsed.skipped ?? []).map((skip) => (
                  <li key={skip.label} className="rounded border p-2 text-sm">
                    <div className="font-medium">
                      {skip.opportunityId === null ? (
                        skip.label
                      ) : (
                        <Link
                          to={`/opportunities/${skip.opportunityId}`}
                          className="hover:underline"
                        >
                          {skip.label}
                        </Link>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      rows {skip.rowIndexes.join(', ')}
                    </div>
                    <div className="mt-1 text-xs">{skip.reason}</div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {parsed.conflicts.length > 0 && (
            <Card className="space-y-3 p-4">
              <h3 className="text-sm font-semibold">
                Conflicting groups — {parsed.conflicts.length}
                {parsed.report.conflicts.likelySeparateDeals > 0 && (
                  <>, {parsed.report.conflicts.likelySeparateDeals} probably two deals</>
                )}
              </h3>
              <p className="text-xs text-muted-foreground">
                Rows are grouped by Customer Name + Opp Name. Decide whether each of these is one
                deal or two <strong>before</strong> committing: there is no reversal, and a bad
                commit is recovered by a point-in-time restore of the database.
              </p>
              <ul className="space-y-2">
                {parsed.conflicts.map((conflict, index) => (
                  <li
                    key={`${conflict.label}-${conflict.kind}-${index}`}
                    className={
                      conflict.likelySeparateDeals
                        ? 'rounded border border-amber-500/60 p-2 text-sm'
                        : 'rounded border p-2 text-sm'
                    }
                  >
                    <div className="font-medium">{conflict.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {conflict.column} · rows {conflict.rowIndexes.join(', ')} on{' '}
                      {conflict.sheets.join(', ')}
                    </div>
                    <div className="mt-1 text-xs">{conflict.detail}</div>
                    <div className="text-xs text-muted-foreground">
                      Resolved: {conflict.resolution}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {parsed.issues.length > 0 && (
            <Card className="space-y-2 p-4">
              <h3 className="text-sm font-semibold">
                Issues — {parsed.report.issues.errors} error
                {parsed.report.issues.errors === 1 ? '' : 's'}, {parsed.report.issues.warnings}{' '}
                warning{parsed.report.issues.warnings === 1 ? '' : 's'}
              </h3>
              <ul className="space-y-1 text-sm">
                {parsed.issues.slice(0, 50).map((issue, index) => (
                  <li key={`${issue.code}-${issue.rowIndex}-${index}`}>
                    <span className="text-xs uppercase text-muted-foreground">
                      {issue.severity}
                    </span>{' '}
                    {issue.rowIndex === null ? 'file' : `row ${issue.rowIndex}`}
                    {issue.column ? ` / ${issue.column}` : ''} — {issue.message}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {((parsed.productWarnings ?? []).length > 0 ||
            (parsed.partsToCreate ?? []).length > 0) && (
            <PartsPreview
              warnings={parsed.productWarnings ?? []}
              toCreate={parsed.partsToCreate ?? []}
            />
          )}

          {step === 'validated' && (
            <div className="flex gap-2">
              <Button
                disabled={!parsed.report.committable || commit.isPending}
                onClick={() => commit.mutate()}
              >
                Commit {parsed.report.reconciliation.opportunityCount} opportunities
              </Button>
              <Button variant="outline" onClick={() => cancel.mutate()}>
                Cancel and delete the uploaded file
              </Button>
            </div>
          )}
        </>
      )}

      {step === 'committing' && progress && (
        <Card className="p-4 text-sm">
          Committing… {progress.committed ?? 0} of {progress.total ?? '?'} opportunities.
        </Card>
      )}

      {step === 'done' && progress && (
        <Card className="space-y-2 p-4">
          <h3 className="text-sm font-semibold">Committed</h3>
          <ul className="space-y-1 text-sm">
            <li>
              {progress.inserted} inserted, {progress.updated} updated, {progress.linesWritten}{' '}
              product lines
            </li>
            {(progress.skipped ?? 0) > 0 && (
              <li>
                {progress.skipped} skipped because they are marked Invalid, and left untouched
              </li>
            )}
            <li>{progress.closuresAppended} closures appended</li>
            <li>
              {progress.accountsCreated.length} accounts, {progress.productsCreated.length} products
              and {progress.contactsCreated} channel contacts created
            </li>
          </ul>
          {progress.warnings.length > 0 && (
            <>
              <h4 className="pt-2 text-sm font-medium">Warnings</h4>
              <ul className="space-y-1 text-sm">
                {progress.warnings.map((warning, index) => (
                  <li key={`${warning.code}-${index}`}>
                    <span className="text-xs uppercase text-muted-foreground">{warning.code}</span>{' '}
                    {warning.message}
                  </li>
                ))}
              </ul>
            </>
          )}
          <Button className="mt-2" variant="outline" onClick={reset}>
            Import another file
          </Button>
        </Card>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* The saved preset                                                  */}
      {/* ---------------------------------------------------------------- */}

      {batch && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold">{batch.preset.name}</h3>
          <p className="text-xs text-muted-foreground">
            The saved column mapping for this firm. Shown rather than re-entered — that is what
            makes the monthly import three clicks.
          </p>
          <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {Object.entries(batch.preset.columns).map(([column, target]) => (
              <div key={column} className="contents">
                <dt className="font-medium">{column}</dt>
                <dd className="text-muted-foreground">{target}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string | number }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * Business Unit and Product Type against the stored parts, before the commit
 * (session 7 part 3). Grouped by code with the rows, because a monthly file
 * repeats the same part on many lines. Warnings never block the commit.
 */
export function PartsPreview({
  warnings,
  toCreate,
}: {
  warnings: ProductWarning[];
  toCreate: { partNumber: string; businessUnit: string }[];
}): React.ReactElement {
  const label = useBusinessUnits().label;
  const groups = new Map<string, ProductWarning[]>();
  for (const warning of warnings) {
    const group = groups.get(warning.code);
    if (group) group.push(warning);
    else groups.set(warning.code, [warning]);
  }
  return (
    <Card className="space-y-2 p-4">
      <h3 className="text-sm font-semibold">
        Parts — {warnings.length} warning{warnings.length === 1 ? '' : 's'}
        {toCreate.length > 0 && `, ${toCreate.length} new part${toCreate.length === 1 ? '' : 's'}`}
      </h3>
      <ul className="space-y-1 text-sm">
        {[...groups].map(([code, group]) => (
          <li key={code}>
            <span className="text-xs uppercase text-muted-foreground">
              {humanize(code.toLowerCase())}
            </span>{' '}
            × {group.length} — rows {group.map((w) => w.rowIndex ?? '?').join(', ')} —{' '}
            {group[0]!.message}
          </li>
        ))}
        {toCreate.map((part) => (
          <li key={part.partNumber}>
            <span className="text-xs uppercase text-muted-foreground">new part</span>{' '}
            <span className="font-mono text-xs">{part.partNumber}</span> will be created under{' '}
            {label(part.businessUnit)}
          </li>
        ))}
      </ul>
    </Card>
  );
}
